import { useEffect } from 'react';
import { Box, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SecurityIcon from '@mui/icons-material/Security';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import CookieIcon from '@mui/icons-material/Cookie';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const privacySections = [
    {
      icon: <PrivacyTipIcon />,
      title: 'Introduction',
      content: "TopSWE ('we,' 'our,' or 'us') respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our website (topswe.com) and services."
    },
    {
      icon: <DataUsageIcon />,
      title: 'Information We Collect',
      content: 'We collect information that you provide directly to us, including:',
      bullets: [
        'Name and email address when you create an account',
        'Payment information when you make a purchase (processed securely through Stripe)',
        'Progress data related to your problem-solving activities',
        'Communications you send to us'
      ]
    },
    {
      icon: <DataUsageIcon />,
      title: 'How We Use Your Information',
      content: 'We use the information we collect to:',
      bullets: [
        'Provide, maintain, and improve our services',
        'Process your transactions',
        'Send you technical notices and support messages',
        'Track your progress and provide personalized learning experiences',
        'Communicate with you about products, services, and educational content'
      ]
    },
    {
      icon: <SecurityIcon />,
      title: 'Data Security',
      content: 'We implement appropriate security measures to protect your personal information. Your payment information is processed through Stripe, which maintains high-security standards. We never store your full credit card information on our servers.'
    },
    {
      icon: <SecurityIcon />,
      title: 'Data Retention',
      content: 'We retain your personal information for as long as necessary to provide you with our services and as required by law. If you delete your account, we will delete or anonymize your information unless required to keep it by law.'
    },
    {
      icon: <PrivacyTipIcon />,
      title: 'Your Rights',
      content: 'You have the right to:',
      bullets: [
        'Access your personal information',
        'Correct inaccurate data',
        'Request deletion of your data',
        'Object to our use of your data',
        'Export your data in a portable format'
      ]
    },
    {
      icon: <CookieIcon />,
      title: 'Cookies',
      content: 'We use cookies and similar tracking technologies to track activity on our website and improve your experience. You can control cookies through your browser settings.'
    },
    {
      icon: <DataUsageIcon />,
      title: 'Third-Party Services',
      content: 'We use trusted third-party services for:',
      bullets: [
        'Payment processing (Stripe)',
        'Analytics (Google Analytics)',
        'Email communications',
        'Content delivery and hosting'
      ]
    },
    {
      icon: <PrivacyTipIcon />,
      title: "Children's Privacy",
      content: 'Our services are not intended for children under 13. We do not knowingly collect or maintain information from persons under 13 years old.'
    },
    {
      icon: <PrivacyTipIcon />,
      title: 'Changes to This Policy',
      content: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the Last updated date.'
    },
    {
      icon: <ContactSupportIcon />,
      title: 'Contact Us',
      content: 'If you have any questions about this privacy policy or our practices, please contact us at:',
      contact: 'topswebusiness@gmail.com'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <Box
        component="section"
        sx={{
          background: (theme) => theme.palette.custom.heroGradient,
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 8, md: 11 }
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Chip label="Last updated: March 2024" />
            <Typography variant="h2" sx={{ fontWeight: 850, letterSpacing: 0, fontSize: { xs: '2.7rem', md: '4.7rem' } }}>
              Privacy Policy
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: 720, lineHeight: 1.55, fontWeight: 450 }}>
              Clear terms for how TopSWE handles account, payment, and learning progress data.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={2.5}>
          {privacySections.map((section) => (
            <Grid item xs={12} md={6} key={section.title}>
              <Paper sx={{ p: { xs: 3, md: 4 }, height: '100%' }}>
                <Stack spacing={2.25}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2.5,
                        bgcolor: 'action.hover',
                        color: 'secondary.main',
                        display: 'grid',
                        placeItems: 'center'
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {section.title}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                    {section.content}
                  </Typography>

                  {section.bullets && (
                    <Stack spacing={1.25}>
                      {section.bullets.map((bullet) => (
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" key={bullet}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'secondary.main', mt: 1, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                            {bullet}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {section.contact && (
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', boxShadow: 'none', textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 750 }}>
                        {section.contact}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Paper sx={{ mt: 3, p: { xs: 3, md: 4 }, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
            We are committed to transparency and protecting your privacy. If you have questions about how we handle your data, contact us any time.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default Privacy;
